/* eslint-disable no-undef */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Stack,
  Alert,
  Paper,
  LinearProgress,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { TextTool } from "utils/TextTool";
import { useFormContext } from "react-hook-form";
import newApi from "service/newApi.js";
import { toast } from "react-toastify";

const FilePicker = ({
  multiple = true,
  accept = "",
  uploadedFiles,
  onFilesSelected,
  onFilesRemoved,
  onUpload,
  uploadProgress,
  uploadStatus,
  maxFiles = 1,
  maxFileSizeInMB = 1,
  fileType = "text", // "text" or "audio"
  folderId = null,
  clientId = null,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [checked, setChecked] = useState([]);
  const [error, setError] = useState("");
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const MAX_SIZE_BYTES = maxFileSizeInMB * 1024 * 1024;
  const { watch } = useFormContext();

  const uploadedFilesMetadata = watch("data_source_files_metadata");

  useEffect(() => {
    setSelectedFiles([]);
    setChecked([]);
    setError("");
  }, [uploadedFiles]);

  const uploadedFileList = Array.isArray(uploadedFiles)
    ? uploadedFiles.map((name, idx) => ({
        id: `uploaded-${idx}`,
        file: { name },
        uploaded: true,
      }))
    : [];

  const displayedFiles = [
    ...uploadedFileList,
    ...selectedFiles.filter(
      (sf) => !uploadedFileList.some((uf) => uf.file.name === sf.file.name),
    ),
  ];

  const anyUploading = displayedFiles.some(
    (f) => !f.uploaded && uploadStatus?.[f.id] === "uploading",
  );

  const activeLocal = selectedFiles.filter(
    (f) =>
      uploadStatus?.[f.id] === "uploading" || uploadStatus?.[f.id] === "done",
  );
  const totalBytesActive = activeLocal.reduce(
    (sum, f) => sum + (f.file.size || 0),
    0,
  );
  const sentBytesActive = activeLocal.reduce((sum, f) => {
    const pct =
      uploadProgress?.[f.id] ?? (uploadStatus?.[f.id] === "done" ? 100 : 0);
    return sum + ((f.file.size || 0) * pct) / 100;
  }, 0);
  const globalPct =
    totalBytesActive > 0
      ? Math.round((sentBytesActive * 100) / totalBytesActive)
      : null;

  const addFiles = (newFiles) => {
    setError("");

    const existingFileNames = [
      ...selectedFiles.map(({ file }) => file.name),
      ...(uploadedFiles || []),
    ];
    const duplicateFiles = [];
    const validFiles = [];

    const availableSlots = maxFiles - selectedFiles.length;

    if (newFiles.length > availableSlots) {
      setError((prev) =>
        prev
          ? `${prev}\nO número máximo de arquivos permitidos é (${maxFiles}).`
          : `O número máximo de arquivos permitidos é (${maxFiles}).`,
      );
      return;
    }

    Array.from(newFiles).forEach((file) => {
      if (accept && !accept.split(",").includes(file.type)) {
        setError((prev) =>
          prev
            ? `${prev}\n"${file.name}" não é um tipo de arquivo válido.`
            : `"${file.name}" não é um tipo de arquivo válido.`,
        );
        return;
      }
      if (existingFileNames.includes(file.name)) {
        duplicateFiles.push(file.name);
        return;
      }

      if (file.size > MAX_SIZE_BYTES) {
        setError((prev) =>
          prev
            ? `${prev}\n"${file.name}" excede o limite de ${maxFileSizeInMB}MB.`
            : `"${file.name}" excede o limite de ${maxFileSizeInMB}MB.`,
        );
        return;
      }
      validFiles.push({
        id: crypto.randomUUID?.() || Date.now() + Math.random(),
        file,
      });
    });

    if (duplicateFiles.length > 0) {
      setError((prev) =>
        prev
          ? `${prev}\nOs arquivos selecionados já estão carregados: ${duplicateFiles.join(", ")}.`
          : `Os arquivos selecionados já estão carregados: ${duplicateFiles.join(", ")}.`,
      );
    }

    const updated = [...selectedFiles, ...validFiles];
    setSelectedFiles(updated);
    setChecked([]);
    if (onFilesSelected) onFilesSelected(updated);
  };

  const handleFileChange = (event) => {
    addFiles(event.target.files);
    event.target.value = null;
  };

  const handleToggle = (id) => () => {
    if (anyUploading) return;
    setChecked((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAllToggle = () => {
    if (anyUploading) return;
    const selectable = selectedFiles.filter(
      (f) =>
        uploadStatus?.[f.id] !== "done" && uploadStatus?.[f.id] !== "uploading",
    );
    const selectableIds = selectable.map((f) => f.id);
    if (checked.length === selectableIds.length) {
      setChecked([]);
    } else {
      setChecked(selectableIds);
    }
  };

  const handleRemoveSelected = () => {
    if (anyUploading) return;
    const filtered = selectedFiles.filter((f) => !checked.includes(f.id));
    setSelectedFiles(filtered);
    setChecked([]);
    if (onFilesSelected) onFilesSelected(filtered.map((f) => f.file));
  };

  const handleUpload = async () => {
    setChecked([]);
    onUpload(selectedFiles, checked);
  };

  const handleDeleteUploadedFile = async (fileName) => {
    if (!folderId || !fileName || !clientId) {
      toast.error(
        "Informações insuficientes para deletar o arquivo (clientId, folderId e fileName são obrigatórios)",
      );
      return;
    }

    const fileKey = `uploaded-${fileName}`;
    setDeletingFiles((prev) => new Set([...prev, fileKey]));

    const removeFromState = ({ showToast = true, message }) => {
      const updatedUploadedFiles = (uploadedFiles || []).filter(
        (file) => file !== fileName,
      );

      if (onFilesRemoved) {
        onFilesRemoved(updatedUploadedFiles, fileName, true);
      }

      const idsToRemove = new Set();
      setSelectedFiles((prev) => {
        const filtered = prev.filter((item) => {
          const shouldRemove = item.file.name === fileName;
          if (shouldRemove) {
            idsToRemove.add(item.id);
          }
          return !shouldRemove;
        });
        return filtered;
      });

      setChecked((prev) => prev.filter((id) => !idsToRemove.has(id)));

      if (showToast) {
        toast.success(
          message ||
            `Arquivo ${fileType === "audio" ? "de áudio" : "de texto"} removido com sucesso`,
        );
      }
    };

    try {
      const endpoint =
        fileType === "audio"
          ? `/v1/event-audios/remove/${encodeURIComponent(clientId)}/${encodeURIComponent(folderId)}/audios/${encodeURIComponent(fileName)}`
          : `/v1/event-texts/remove/${encodeURIComponent(clientId)}/${encodeURIComponent(folderId)}/texts/${encodeURIComponent(fileName)}`;

      console.log("Tentando deletar arquivo:", {
        endpoint,
        fileType,
        clientId,
        folderId,
        fileName,
      });

      const response = await newApi.delete(endpoint);

      if (response.status >= 200 && response.status < 300) {
        removeFromState({ showToast: true });
      }
    } catch (error) {
      console.error("Erro ao deletar arquivo:", error);
      console.log("Detalhes da resposta de erro:", {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        headers: error?.response?.headers,
      });

      if (error?.response?.status === 404) {
        removeFromState({
          showToast: true,
          message:
            "Arquivo não encontrado. Removendo da listagem para sincronizar com o servidor.",
        });
        return;
      }

      toast.error(
        `Erro ao remover arquivo ${fileType === "audio" ? "de áudio" : "de texto"}: ${
          error?.response?.data?.message ||
          error?.message ||
          "Erro desconhecido"
        }`,
      );
    } finally {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        return newSet;
      });
    }
  };
  const handleDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <Box>
      <input
        type="file"
        multiple={multiple}
        accept={accept}
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {globalPct !== null && (
        <Box mt={2}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Progresso total: <b>{globalPct}%</b>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={globalPct}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      )}

      {displayedFiles.length < maxFiles && (
        <Box
          mt={2}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          component={Paper}
          variant="outlined"
          sx={{
            p: 2,
            textAlign: "center",
            borderStyle: "dashed",
            color: "#666",
            backgroundColor: "#fff",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <i className={"ni ni-cloud-upload-96 text-#404040"} />
          <span
            style={{
              fontSize: "0.9em",
            }}
          >
            Escolha um arquivo ou arraste para carregar
          </span>
        </Box>
      )}

      {error && (
        <Box mt={2}>
          <Alert severity="warning">{error}</Alert>
        </Box>
      )}

      {displayedFiles.length > 0 && (
        <Box mt={2}>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon>
                {!anyUploading &&
                  displayedFiles.some(
                    (f) => !f.uploaded && uploadStatus[f.id] !== "done",
                  ) && (
                    <Checkbox
                      edge="start"
                      onChange={handleSelectAllToggle}
                      checked={
                        checked.length ===
                          displayedFiles.filter(
                            (f) =>
                              !f.uploaded &&
                              uploadStatus[f.id] !== "done" &&
                              uploadStatus[f.id] !== "uploading",
                          ).length &&
                        displayedFiles.filter(
                          (f) =>
                            !f.uploaded &&
                            uploadStatus[f.id] !== "done" &&
                            uploadStatus[f.id] !== "uploading",
                        ).length > 0
                      }
                      indeterminate={
                        checked.length > 0 &&
                        checked.length <
                          displayedFiles.filter(
                            (f) =>
                              !f.uploaded &&
                              uploadStatus[f.id] !== "done" &&
                              uploadStatus[f.id] !== "uploading",
                          ).length
                      }
                    />
                  )}
              </ListItemIcon>
              <ListItemText primary="Arquivos" />
            </ListItem>

            <Divider />

            {displayedFiles.map(({ id, file, uploaded }) => (
              <Box key={id}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon>
                    {uploaded ? (
                      <>✅</>
                    ) : uploadStatus?.[id] !== "done" ? (
                      <Checkbox
                        edge="start"
                        checked={checked.includes(id)}
                        onChange={handleToggle(id)}
                        disabled={
                          uploadStatus[id] === "done" &&
                          uploadStatus[f.id] === "uploading"
                        }
                      />
                    ) : (
                      <>✅</>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <span>
                          {file.name}
                          {typeof uploadedFilesMetadata?.find(
                            (f) => f.id === file.name,
                          )?.total_events === "number"
                            ? ` - Qtd Eventos: ${uploadedFilesMetadata.find((f) => f.id === file.name).total_events}`
                            : ""}
                        </span>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="caption">
                            <b>
                              {file.size ? TextTool.formatBytes(file.size) : ""}
                            </b>
                          </Typography>
                          <Typography
                            color={
                              uploaded
                                ? "success"
                                : uploadStatus?.[id] === "uploading"
                                  ? "info"
                                  : uploadStatus?.[id] === "done"
                                    ? "success"
                                    : uploadStatus?.[id] === "error"
                                      ? "error"
                                      : "primary"
                            }
                          >
                            <b>
                              <i>
                                {deletingFiles.has(id)
                                  ? "Removendo..."
                                  : uploaded
                                    ? "Enviado"
                                    : uploadStatus?.[id] === "uploading"
                                      ? `Enviando... ${Math.max(0, uploadProgress?.[id] || 0)}%`
                                      : uploadStatus?.[id] === "done"
                                        ? "Enviado"
                                        : uploadStatus?.[id] === "error"
                                          ? "Houve um erro"
                                          : ""}
                              </i>
                            </b>
                          </Typography>
                          {uploaded && folderId && clientId && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteUploadedFile(file.name)
                              }
                              disabled={deletingFiles.has(id)}
                              title="Remover arquivo enviado"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItem>
                {uploadProgress && uploadProgress[id] != null && (
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress[id] ?? 0}
                      sx={{ height: 6, borderRadius: 1 }}
                    />
                  </Box>
                )}
                <Divider />
              </Box>
            ))}

            {displayedFiles.length && displayedFiles[0].file.size && (
              <Typography
                align="right"
                sx={{ marginTop: "10px", marginRight: "30px" }}
              >
                {displayedFiles.length} arquivo(s) carregado(s) {" | "}
                <b>
                  {TextTool.formatBytes(
                    displayedFiles.reduce(
                      (sum, f) => sum + (f.file.size || 0),
                      0,
                    ),
                  )}
                </b>
              </Typography>
            )}
          </List>

          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="outlined"
              color="error"
              disabled={checked.length === 0 || anyUploading}
              onClick={handleRemoveSelected}
            >
              Remover
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={checked.length === 0}
              onClick={handleUpload}
            >
              Enviar
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FilePicker;
